<template>
  <div class="ask-ai-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">Provide Additional Context</h1>
      <div :class="['balance-text', balanceClass]">
        You have a balance of ₹{{ creditBalance }}
      </div>
    </div>

    <!-- Context Card -->
    <div class="question-card">
      <p v-if="originalQuestion" class="original-question">
        <strong>Your Question:</strong> {{ originalQuestion }}
      </p>

      <label for="context" class="label">
        Additional Context <small>(Optional)</small>
      </label>
      <textarea
        id="context"
        ref="contextField"
        v-model="context"
        placeholder="Provide context if needed..."
        rows="12"
      ></textarea>
      <p class="instructions">
        This helps the AI give more accurate responses.
      </p>
      <p class="char-limit">{{ context.length }}/200</p>

      <div class="button-group">
        <button @click="goBack" class="action-button secondary">
          Back
        </button>
        <button @click="submitForm" class="action-button" :disabled="loading">
          {{ loading ? 'Submitting...' : 'Submit' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContextPage',
  data() {
    return {
      context: '',
      loading: false
    };
  },
  computed: {
    originalQuestion() {
      return localStorage.getItem('user_question');
    },
    creditBalance() {
      return parseFloat(localStorage.getItem('credit_balance') || '0');
    },
    balanceClass() {
      if (this.creditBalance < 50) return 'red-balance';
      if (this.creditBalance <= 150) return 'yellow-balance';
      return 'green-balance';
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'AskAI' });
    },
    submitForm() {
      if (this.loading) return;
      this.loading = true;

      const token = localStorage.getItem('auth_token');
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }

      const question = localStorage.getItem('user_question');
      if (!question) {
        alert('Question not found. Please go back and submit your question.');
        this.loading = false;
        return this.$router.push({ name: 'AskAI' });
      }

      if (this.creditBalance <= 0) {
        alert('Insufficient credit. Please top up to continue.');
        this.loading = false;
        return;
      }

      // Store context for LoadingPage
      localStorage.setItem('user_context', this.context);
      // Navigate to the loading animation
      this.$router.push({ name: 'Loading' });
    }
  },
  mounted() {
    if (!localStorage.getItem('auth_token')) {
      this.$router.push({ name: 'Login' });
    }
    this.$nextTick(() => {
      this.$refs.contextField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.$refs.contextField?.focus();
    });
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.ask-ai-page {
  padding: var(--space-md);
  max-width: var(--content-max-width, 1200px);
  margin: 0 auto;
  font-family: var(--font-sans, 'Poppins', sans-serif);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
}

.page-title {
  font-size: var(--font-size-xl, 1.5rem);
  color: var(--color-text, #1f2937);
}

.balance-text {
  font-size: var(--font-size-base, 1rem);
  font-weight: 500;
}

.red-balance { color: var(--color-danger, #dc2626); }
.yellow-balance { color: var(--color-warning, #d97706); }
.green-balance { color: var(--color-success, #16a34a); }

.question-card {
  background: var(--color-bg, #ffffff);
  padding: var(--space-lg, 1.5rem);
  border-radius: var(--radius-lg, 0.5rem);
  box-shadow: var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1));
}

.original-question {
  margin-bottom: var(--space-md);
  font-size: var(--font-size-base, 1rem);
}

.label {
  font-weight: 600;
  display: block;
  margin-bottom: var(--space-sm);
}

textarea {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--color-muted);
  border-radius: var(--radius-sm);
  resize: none;
  margin-bottom: var(--space-sm);
}

.instructions,
.char-limit {
  margin-top: var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-muted, #6b7280);
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-md);
  margin-top: var(--space-md);
}

.action-button {
  padding: var(--space-sm) var(--space-md);
  background: var(--color-accent, #3b82f6);
  color: var(--color-on-accent, #ffffff);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}

.action-button:disabled {
  background: var(--color-muted, #d1d5db);
  color: var(--color-text, #6b7280);
}

.action-button:hover:enabled {
  background: var(--color-accent-dark, #2563eb);
  transform: translateY(-1px);
}

.action-button.secondary {
  background: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
}

.action-button.secondary:hover {
  background: var(--color-accent-dark, #2563eb);
  color: var(--color-on-accent);
}
</style>
